import React, { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Search, X, Check, ChevronLeft, ChevronRight } from 'lucide-react'
import api from '../services/api'
import toast from 'react-hot-toast'

const UserManagement = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editUser, setEditUser] = useState(null)
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '', phone: '', role: 'consumer', companyName: '' })

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true)
      const params = new URLSearchParams({ page, limit: 20 })
      if (search) params.append('search', search)
      if (roleFilter) params.append('role', roleFilter)
      const res = await api.get(`/users?${params}`)
      if (res.data?.success) {
        setUsers(res.data.data.users)
        setPagination(res.data.data.pagination)
      }
    } catch (err) {
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, [roleFilter])

  const handleSearch = (e) => {
    e.preventDefault()
    fetchUsers(1)
  }

  const openCreate = () => {
    setEditUser(null)
    setForm({ email: '', password: '', firstName: '', lastName: '', phone: '', role: 'consumer', companyName: '' })
    setShowModal(true)
  }

  const openEdit = (u) => {
    setEditUser(u)
    setForm({ email: u.email, password: '', firstName: u.firstName || '', lastName: u.lastName || '', phone: u.phone || '', role: u.role, companyName: u.companyName || '' })
    setShowModal(true)
  }

  const handleSave = async () => {
    try {
      if (editUser) {
        const payload = { ...form }
        if (!payload.password) delete payload.password
        await api.put(`/users/${editUser.id}`, payload)
        toast.success('User updated')
      } else {
        if (!form.email || !form.password) { toast.error('Email and password required'); return }
        await api.post('/users', form)
        toast.success('User created')
      }
      setShowModal(false)
      fetchUsers(pagination.page)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed')
    }
  }

  const handleDelete = async (u) => {
    if (!confirm(`Delete user ${u.email}?`)) return
    try {
      await api.delete(`/users/${u.id}`)
      toast.success('User deleted')
      fetchUsers(pagination.page)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed')
    }
  }

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { isActive: !u.isActive })
      toast.success(`User ${u.isActive ? 'deactivated' : 'activated'}`)
      fetchUsers(pagination.page)
    } catch (err) {
      toast.error('Update failed')
    }
  }

  const roleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-700',
      manufacturer: 'bg-blue-100 text-blue-700',
      consumer: 'bg-gray-100 text-gray-700',
    }
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[role] || styles.consumer}`}>{role}</span>
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500 mt-1">{pagination.total} total users</p>
        </div>
        <button onClick={openCreate} className="flex items-center space-x-2 px-4 py-2.5 bg-[#1E4D40] text-white rounded-xl hover:bg-[#163a30] transition-colors font-medium text-sm">
          <Plus className="h-4 w-4" />
          <span>Add User</span>
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users by name or email..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20 focus:border-[#1E4D40]"
            />
          </form>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20">
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manufacturer">Manufacturer</option>
            <option value="consumer">Consumer</option>
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E4D40]" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="text-left py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="text-left py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Company</th>
                  <th className="text-left py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Phone</th>
                  <th className="text-center py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-center py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="text-right py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-3">
                      <div className="flex items-center space-x-3">
                        <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || 'U')}&background=1E4D40&color=fff&size=32`} alt="" className="h-8 w-8 rounded-full" />
                        <div>
                          <p className="font-medium text-gray-900">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3">{roleBadge(u.role)}</td>
                    <td className="py-3 px-3 hidden md:table-cell text-gray-600">{u.companyName || '-'}</td>
                    <td className="py-3 px-3 hidden lg:table-cell text-gray-600">{u.phone || '-'}</td>
                    <td className="py-3 px-3 text-center">
                      <button onClick={() => toggleActive(u)} className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="py-3 px-3 text-center text-xs text-gray-500">{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-end space-x-1">
                        <button onClick={() => openEdit(u)} className="p-1.5 text-gray-400 hover:text-[#1E4D40] hover:bg-[#1E4D40]/10 rounded-lg transition-colors"><Edit2 className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(u)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
            <div className="flex space-x-2">
              <button onClick={() => fetchUsers(pagination.page - 1)} disabled={pagination.page <= 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => fetchUsers(pagination.page + 1)} disabled={pagination.page >= pagination.pages}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">{editUser ? 'Edit User' : 'Add User'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                  <input value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                  <input value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{editUser ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20">
                  <option value="consumer">Consumer</option>
                  <option value="manufacturer">Manufacturer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {form.role === 'manufacturer' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
                  <input value={form.companyName} onChange={(e) => setForm({...form, companyName: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20" />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end space-x-3 p-5 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm font-medium bg-[#1E4D40] text-white rounded-xl hover:bg-[#163a30] transition-colors flex items-center space-x-1">
                <Check className="h-4 w-4" />
                <span>{editUser ? 'Update' : 'Create'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
